// Custom hook that manages fetching and pagination for project_filter attribute
// names from `ppa/simulation/get_attribute_names`.

import React, { useCallback, useEffect, useMemo } from 'react';

import axios from 'axios';
import { useInfiniteQuery } from '@tanstack/react-query';

import { ppaSimulationUrls } from 'config/urls';

import type { PPAInputConfig, PPASelectOption } from 'types/ppa';
import {
    ATTRIBUTE_NAMES_LIST_HEIGHT,
    ATTRIBUTE_NAMES_PAGE_SIZE,
    ATTRIBUTE_NAMES_PREFETCH_UNTIL_COUNT,
    dedupeSelectValuesByKey,
    optionValueKey,
    parseAttributeNamesResponse,
} from './PPAField.helpers';
import type { PpaProjectFilterInclusion } from './PPAField.helpers';

export type UseAttributeNamesResult = {
    /** Final deduplicated options from all loaded pages. */
    dynamicOptions: PPASelectOption[];
    /** True while the first page is loading. */
    dynamicOptionsLoading: boolean;
    /** True while any page (including first) is loading — use for the Select `loading` prop. */
    projectFilterAsyncSelectLoading: boolean;
    /** Whether this field drives an async select (enables scroll-to-load). */
    isProjectFilterAsyncSelect: boolean;
    /** Controlled value coerced to string keys for async selects. */
    asyncProjectFilterSelectValue: any;
    /** Scroll handler that triggers next-page fetch near the bottom of the dropdown. */
    handleAttributeNamesPopupScroll: (e: React.UIEvent<HTMLDivElement>) => void;
    /** Virtual list height to use on the Select. */
    listHeight: number | undefined;
};

export function useAttributeNames(
    inputConfig: PPAInputConfig,
    disabled: boolean,
    value: any,
    priorProjectFilterInclusions: PpaProjectFilterInclusion[],
): UseAttributeNamesResult {
    const attributeTypeForRequest = inputConfig.attribute_type ?? '';

    const shouldFetchDynamicOptions =
        inputConfig.input_id === 'project_filters' &&
        !disabled &&
        !inputConfig.options?.length &&
        Boolean(inputConfig.dimension) &&
        Boolean(attributeTypeForRequest);

    const inclusionFilters = priorProjectFilterInclusions.filter((row) => {
        if (Array.isArray(row.attribute_ex_id)) return row.attribute_ex_id.length > 0;
        return String(row.attribute_ex_id ?? '').length > 0;
    });

    const inclusionQueryKey = JSON.stringify(inclusionFilters);

    const {
        data: attributeNamesInfiniteData,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        isLoading: attributeNamesInitialLoading,
    } = useInfiniteQuery({
        queryKey: [
            'ppa-get-attribute-names',
            inputConfig.dimension,
            attributeTypeForRequest,
            inclusionQueryKey,
            ATTRIBUTE_NAMES_PAGE_SIZE,
        ],
        enabled: shouldFetchDynamicOptions,
        initialPageParam: 0,
        queryFn: async ({ pageParam }) => {
            const { data } = await axios.post(ppaSimulationUrls.getAttributeNamesUrl, {
                dimension: inputConfig.dimension ?? 'dim1',
                attribute_type: attributeTypeForRequest,
                search: '',
                offset: pageParam,
                limit: ATTRIBUTE_NAMES_PAGE_SIZE,
                filters: {
                    inclusion: inclusionFilters,
                },
            });
            return parseAttributeNamesResponse(data);
        },
        getNextPageParam: (lastPage, allPages) => {
            if (!lastPage.length || lastPage.length < ATTRIBUTE_NAMES_PAGE_SIZE) return undefined;
            return allPages.reduce((sum, page) => sum + page.length, 0);
        },
    });

    const pagedAttributeOptions = useMemo(
        () => attributeNamesInfiniteData?.pages.flatMap((p) => p) ?? [],
        [attributeNamesInfiniteData],
    );

    const dynamicOptions: PPASelectOption[] = useMemo(() => {
        if (!shouldFetchDynamicOptions) return [];
        const byValue = new Map<string, PPASelectOption>();
        pagedAttributeOptions.forEach((opt) => {
            const k = optionValueKey(opt?.value);
            if (k == null || byValue.has(k)) return;
            byValue.set(k, {
                ...opt,
                value: k,
                label: opt.label != null && opt.label !== '' ? String(opt.label) : k,
            });
        });
        return Array.from(byValue.values());
    }, [shouldFetchDynamicOptions, pagedAttributeOptions]);

    const dynamicOptionsLoading = shouldFetchDynamicOptions && attributeNamesInitialLoading;

    const isProjectFilterAsyncSelect =
        shouldFetchDynamicOptions &&
        (inputConfig.input_type === 'singleselect' || inputConfig.input_type === 'multiselect');

    /** Coerce to string keys so controlled `value` matches `options` after pagination (no duplicate ticks). */
    const asyncProjectFilterSelectValue = useMemo(() => {
        if (!isProjectFilterAsyncSelect) return value;
        if (inputConfig.input_type === 'multiselect') {
            if (!Array.isArray(value)) return value;
            return dedupeSelectValuesByKey(value).map((v) => String(v));
        }
        if (inputConfig.input_type === 'singleselect') {
            if (value === undefined || value === null || value === '') return value;
            return String(value);
        }
        return value;
    }, [isProjectFilterAsyncSelect, inputConfig.input_type, value]);

    const handleAttributeNamesPopupScroll = useCallback(
        (e: React.UIEvent<HTMLDivElement>) => {
            if (!isProjectFilterAsyncSelect || !hasNextPage || isFetchingNextPage) return;
            const target = e.target as HTMLDivElement;
            if (target.scrollTop + target.offsetHeight >= target.scrollHeight - 24) {
                void fetchNextPage();
            }
        },
        [isProjectFilterAsyncSelect, hasNextPage, isFetchingNextPage, fetchNextPage],
    );

    const projectFilterAsyncSelectLoading =
        dynamicOptionsLoading || (isProjectFilterAsyncSelect && isFetchingNextPage);

    // Prefetch additional pages until ATTRIBUTE_NAMES_PREFETCH_UNTIL_COUNT options are loaded.
    useEffect(() => {
        if (!isProjectFilterAsyncSelect) return;
        if (!hasNextPage || isFetchingNextPage || attributeNamesInitialLoading) return;
        const pages = attributeNamesInfiniteData?.pages;
        if (!pages?.length) return;
        const lastPage = pages[pages.length - 1];
        if (!lastPage.length || lastPage.length < ATTRIBUTE_NAMES_PAGE_SIZE) return;

        const totalLoaded = pages.reduce((sum, page) => sum + page.length, 0);
        if (totalLoaded >= ATTRIBUTE_NAMES_PREFETCH_UNTIL_COUNT) return;

        void fetchNextPage();
    }, [
        isProjectFilterAsyncSelect,
        hasNextPage,
        isFetchingNextPage,
        attributeNamesInitialLoading,
        attributeNamesInfiniteData?.pages,
        fetchNextPage,
    ]);

    return {
        dynamicOptions,
        dynamicOptionsLoading,
        projectFilterAsyncSelectLoading,
        isProjectFilterAsyncSelect,
        asyncProjectFilterSelectValue,
        handleAttributeNamesPopupScroll,
        listHeight: isProjectFilterAsyncSelect ? ATTRIBUTE_NAMES_LIST_HEIGHT : undefined,
    };
}
